import React from 'react';
import { cn } from '@/lib/utils';
import type { PersonSessionSummary } from '@/lib/api/people';
import QualificationStatusSelect from '@/components/sessions/QualificationStatusSelect';
import { useUpdateSessionQualificationStatus } from '@/hooks/sessions/useUpdateSessionQualificationStatus';

interface PersonSessionsListProps {
  sessions: PersonSessionSummary[];
  selectedSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds: number | undefined | null): string => {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (!m) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const PersonSessionsList: React.FC<PersonSessionsListProps> = ({
  sessions,
  selectedSessionId,
  onSelectSession,
}) => {
  const updateQualificationStatus = useUpdateSessionQualificationStatus();

  if (sessions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        No sessions found
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 overflow-y-auto p-3">
      {sessions.map((session) => {
        const isSelected = selectedSessionId === session.id;
        const qualificationStatus =
          session.updated_qualification_status || session.qualification_status;
        const date = formatDate(session.started_at);

        return (
          <button
            key={session.id}
            type="button"
            onClick={() => onSelectSession(session.id)}
            className={cn(
              'w-full rounded-xl border card-border card-secondary-bg p-4 text-left transition hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm',
              isSelected && 'border-blue-500 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-950/30 shadow-sm'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 min-w-0 flex-1">
                <div className="text-sm font-semibold text-heading">
                  {date}
                </div>
                {session.duration_seconds != null && (
                  <div className="text-xs text-muted-foreground">
                    {formatDuration(session.duration_seconds)}
                  </div>
                )}
              </div>
              <QualificationStatusSelect
                value={qualificationStatus || undefined}
                onChange={(value) =>
                  updateQualificationStatus.mutate({
                    sessionId: session.id,
                    qualification_status: value,
                  })
                }
                align="end"
                triggerType="span"
                className="shrink-0"
              />
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default PersonSessionsList;
